<script>
import Axios from 'axios';
import { mapActions, mapState } from 'vuex';
import i18n from '@/plugins/i18n';

export default {
    props: {
        selectedCompanyId: {
            type: Number,
            required: true,
        },
    },

    data() {
        return {
            info: {
                logoDirectory: null,
                companyName: null,
                companyFullName: null,
                address: null,
                location: null,
            },

            companyNameExists: false,
            companyFullNameExists: false,

            countries: [],
            states: [],
            provinces: [],

            selectedCountry: null,
            selectedState: null,
            selectedProvince: null,

            canChangeLocation: false,

            fallbackValue: null,

            addressFieldActive: false,

            timerDone: true,
        }
    },

    watch: {
        selectedCountry(newVal, oldVal) {
            if (newVal && newVal != oldVal) {
                this._getStatesByCountryId(newVal.add_country_id);
            }
        },

        selectedState(newVal, oldVal) {
            if (!this.selectedCountry) return;

            if (newVal && newVal != oldVal && this.selectedCountry.has_state) {
                this._getProvincesByStateId(newVal.add_state_id);
            }
        },
    },

    computed: {
        isAdmin() {
            if (this.$user) {
                return this.$user.role_id == 1;
            } else {
                return false;
            }
        },

        ...mapState('company', ['logo']),

        fields: () => {
            return [
                { key: 'company_name', data: 'companyName' },
                { key: 'company_fullname', data: 'companyFullName' },
                { key: 'adress', data: 'addres' },
            ];
        },

        addressLengthColor() {
            if (this.info.address == null) return 'black';

            const ratio = this.info.address.length / 1000;
            if (ratio < .35) {
                return 'var(--sustainfinity-green)';
            } else if (ratio >= .35 && ratio < .7) {
                return 'var(--icon-yellow)';
            } else if (ratio >= .7 && ratio <= 1) {
                return 'var(--invalid-input)';
            } else {
                return 'var(--sustainfinity-navy)';
            }
        },
    },

    mounted() {
        this._getFirstSection();
    },

    methods: {
        ...mapActions('company', [
            "getFirstSection",
            "updateLogo",
            "updateCompany",
            "getCountries",
            "getStatesByCountryId",
            "getProvincesByStateId",
            "getProvincesByCountryId",
            "getAndSetCompanyLogo",
        ]),

        _getFirstSection() {
            this.getFirstSection()
                .then(res => {
                    if (res.data.company_name && res.data.company_name.length > 0) {
                        this.companyNameExists = true;
                    }

                    if (res.data.company_fullname && res.data.company_fullname.length > 0) {
                        this.companyFullNameExists = true;
                    }

                    this.info.logoDirectory = res.data.logo;
                    this.info.companyName = res.data.company_name;
                    this.info.companyFullName = res.data.company_fullname;
                    this.info.address = res.data.adress;

                    this.info.location = res.data.location;

                    if (res.data.location.country) {
                        this.selectedCountry = res.data.location.country;
                    }

                    if (res.data.location.state) {
                        this.selectedState = res.data.location.state;
                    }

                    if (res.data.location.province) {
                        this.selectedProvince = res.data.location.province;
                    }

                    if (!this.selectedCountry || !this.selectedState || this.$user.role_id == 1) {
                        this.canChangeLocation = true;
                    }
                })
                .finally(() => {
                    this._getCountries();
                })
        },

        _getCountries() {
            this.getCountries()
                .then(res => {
                    this.countries = res.data;
                    
                    if (this.selectedCountry) {
                        this.selectedCountry = res.data.find(x => x.add_country_id == this.selectedCountry.add_country_id);
                    }
                })
                .catch((err) => {
                    console.error(err);
                })
        },

        _getStatesByCountryId(id) {
            this.getStatesByCountryId({country_id: id})
                .then(res => {
                    this.states = res.data;
                    
                    if (this.selectedState) {
                        this.selectedState = res.data.find(x => x.add_state_id == this.selectedState.add_state_id);
                    }
                })
                .catch((err) => {
                    console.error(err);
                })
        },

        _getProvincesByStateId(id) {
            this.getProvincesByStateId({state_id: id})
                .then(res => {
                    this.provinces = res.data;
                    
                    if (this.selectedProvince) {
                        this.selectedProvince = res.data.find(x => x.add_province_id == this.selectedProvince.add_province_id);
                    }
                })
                .catch((err) => {
                    console.error(err);
                })
        },

        _getProvincesByCountryId(id) {
            this.getProvincesByCountryId({country_id: id})
                .then(res => {
                    this.provinces = res.data;
                })
                .catch((err) => {
                    console.error(err);
                })
        },

        openFilePicker() {
            this.$refs.fileInput.click();
        },

        handleFileSelect(event) {
            const selectedFile = event.target.files[0];
            const sizeInKB = selectedFile.size / 1024;
            const maxSizeInKB = 1024;

            if (selectedFile && sizeInKB < maxSizeInKB) {
                this.updateCompanyLogo(selectedFile);
            } else {
                this.$notify('error', i18n.t('companyProfile.sizeWarning'), true);
            }
        },

        updateCompanyLogo(selectedFile) {
            const formData = new FormData();
            formData.append('files', selectedFile);
            formData.append('company_id', this.selectedCompanyId)

            const uploadUrl = '/api/company/uploadcompanylogo';

            Axios.post(uploadUrl, formData, { withCredentials: true })
                .then(res => {
                    this.getAndSetCompanyLogo({logo: selectedFile});
                    this.$notify('success', res.data.messageName);
                })
                .catch(({ response }) => {
                    this.$notify('error', response.data.messageName);
                })
        },

        downloadLogo() {
            const img = document.querySelector("#companyLogo");
            const link = document.createElement("a");

            link.href = img.src;
            link.download = "logo.png";
            link.click();
        },

        _updateCompany(event, key) {
            const params = {
                company_id: this.selectedCompanyId,
                body: {
                    [`${key}`]: event.target.value
                }
            }

            this.updateCompany(params)
                .then(res => {
                    if (key === 'company_name') {
                        this.companyNameExists = true;
                    } else if (key === 'company_fullname') {
                        this.companyFullNameExists = true;
                    }

                    this.$notify('success', res.data.messageName);
                })
                .catch(({ response }) => {
                    event.target.value = this.fallbackValue;
                    event.target.blur();

                    const valueField = this.fields.find(item => item.key == key);
                    this.info[`${valueField.data}`] = this.fallbackValue;

                    this.$notify('error', response.data.messageName);
                })
                .finally(() => {
                    this.setFallbackValue(null);
                })
        },

        _updateCompanyLocation(obj, key) {
            const params = {
                company_id: this.selectedCompanyId,
                body: {
                    [key]: obj[key]
                }
            }

            this.updateCompany(params)
                .then(res => {
                    this.$notify('success', res.data.messageName);
                })
                .catch(({ response }) => {
                    this.$notify('error', response.data.messageName);
                })
                .finally(() => {
                    this.setFallbackValue(null);
                })
        },

        /* setters */

        setFallbackValue(value) {
            this.fallbackValue = value;
        },

        /* utils */

        contactUs(condition) {
            if (this.isAdmin) return;
            if (this.$timerDone.value && condition) {
                this.$notify('info', i18n.t('userProfile.contactUs'), true);
                this.$timerDone.value = this.$startTimer(5000, this.$timerDone);
            }
        },

    }
}
</script>

<template>
    <div class="first-section">
        <div v-if="logo == null" class="logo-container" @click="openFilePicker"
            v-sust-tooltip="{ text: $t('companyProfile.modal1.uploadLogo') }">
            <input type="file" ref="fileInput" style="display: none;" accept="image/jpg,image/png" @change="handleFileSelect">
            <img src="/images/uploadLogo.png" alt="...">
        </div>
        <div v-else class="logo-container" @click="openFilePicker"
            v-sust-tooltip="{ text: $t('companyProfile.modal1.changeLogo') }">
            <input type="file" ref="fileInput" style="display: none;" accept="image/jpg,image/png" @change="handleFileSelect">
            <img :src="logo" alt="..." id="companyLogo">
        </div>
        <button v-if="logo"
            class="icon-button download-logo"
            v-sust-tooltip="{text: $t('companyProfile.modal1.downloadLogo')}"
            @click="downloadLogo()"
        >
            <i class="fa-solid fa-download"></i>
        </button>

        <div class="section-col">
            <div class="section-col-group">
                <div class="section-col-item">
                    <label for="input">
                        {{ $t('companyProfile.modal1.name') }}
                    </label>
                    <input type="text" name="input" maxlength="50" v-model="info.companyName"
                        @focus="setFallbackValue(info.companyName)" @blur="setFallbackValue(null)"
                        @change="_updateCompany($event, 'company_name')" :disabled="companyNameExists && !isAdmin" required
                        @mouseover="contactUs(companyNameExists)">
                </div>
            </div>

            <div class="section-col-group">
                <div class="section-col-item" style="border-right: thin solid lightgray; padding-right: 1em;">
                    <label for="input">
                        {{ $t('companyOperations.modal.country') }}
                    </label>
                    <select v-if="canChangeLocation"
                        name="input" v-model="selectedCountry"
                        @change="_updateCompanyLocation(selectedCountry, 'add_country_id')"
                    >
                        <option v-for="item in countries" :key="item.add_country_id"
                            :value="item"
                        >
                            {{ item.add_country_name }}
                        </option>
                    </select>
                    <input type="text" v-model="selectedCountry.add_country_name" v-else disabled>
                </div>
                <div
                    class="section-col-item" 
                    style="border-right: thin solid lightgray; padding-right: 1em;"
                >
                    <!-- 
                        the complexity here is caused by provinces in no-state countries being registered to the database as states.
                        therefore, the label should change from "state" to "province" accordingly in this manner.
                        Since we had to handle this, we also have to show "State / Province" when there is no country selected.
                    -->
                    <label for="input" v-if="selectedCountry && selectedCountry.has_state">
                        {{ $t('companyOperations.modal.state') }}
                    </label>
                    <label for="input" v-else-if="selectedCountry && !selectedCountry.has_state">
                        {{ $t('companyOperations.modal.province') }}
                    </label>
                    <label for="input" v-else>
                        {{ $t('companyOperations.modal.state') }} / {{ $t('companyOperations.modal.province') }}
                    </label>
                    <select v-if="canChangeLocation"
                        name="input" v-model="selectedState" :disabled="states.length == 0"
                        @change="_updateCompanyLocation(selectedState, 'add_state_id')"
                    >
                        <option v-for="item in states" :key="item.add_state_id"
                            :value="item"
                        >
                            {{ item.add_state_name }}
                        </option>
                    </select>
                    <input type="text" v-model="selectedState.add_state_name" v-else disabled>
                </div>
                <div v-if="selectedCountry && selectedCountry.has_state" 
                    class="section-col-item" 
                    style="border-right: thin solid lightgray; padding-right: 1em;"
                >
                    <label for="input">
                        {{ $t('companyOperations.modal.province') }}
                    </label>
                    <select v-if="canChangeLocation"
                        name="input" v-model="selectedProvince" :disabled="provinces.length == 0"
                        @change="_updateCompanyLocation(selectedProvince, 'add_province_id')"
                    >
                        <option v-for="item in provinces" :key="item.add_province_id"
                            :value="item"
                        >
                            {{ item.add_province_name }}
                        </option>
                    </select>
                    <input type="text" v-model="selectedProvince.add_province_name" v-else disabled>
                </div>
            </div>
        </div>

        <div class="section-col">
            <div class="section-col-group">
                <div class="section-col-item">
                    <label for="input" class="has-required-field">
                        {{ $t('companyProfile.modal1.fullname') }}

                        <div v-if="!info.companyFullName || info.companyFullName.length === 0">
                            {{ $t('companyProfile.requiredWarning') }}
                        </div>
                    </label>
                    <input type="text" name="input" maxlength="100" v-model="info.companyFullName"
                        @focus="setFallbackValue(info.companyFullName)" @blur="setFallbackValue(null)"
                        @change="_updateCompany($event, 'company_fullname')" :disabled="companyFullNameExists && !isAdmin" required
                        @mouseover="contactUs(companyFullNameExists)">
                </div>
            </div>

            <div class="section-col-group">
                <div class="section-col-item">
                    <label for="input">
                        {{ $t('companyProfile.modal1.address') }}
                        <span v-if="addressFieldActive" style="font-size: .65em; line-height: 0;"
                            :style="{ color: addressLengthColor }">
                            {{ info.address.length }}/1000
                        </span>
                    </label>
                    <input type="text" name="input" maxlength="1000" v-model="info.address" @focus="() => {
                        setFallbackValue(info.address);
                        addressFieldActive = true;
                    }" @blur="() => {
                        setFallbackValue(null);
                        addressFieldActive = false;
                    }" @change="_updateCompany($event, 'adress')">
                </div>
            </div>
        </div>
    </div>
</template>

<style lang='scss' scoped>
.first-section {
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: flex-start;
    width: 100%;

    padding: 1em;

    position: relative;
    > .download-logo {
        position: absolute;
        bottom: 1em;
        left: 1em;
    }
}

.logo-container {
    width: 160px;
    height: 160px;
    border-radius: 50%;
    overflow: hidden;
    cursor:pointer;
    box-shadow: 0 .25em .25em .1em gray;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
    background-color: white;
    
    >img {
        width: 100%;
        height: 100%;
        object-fit: contain;
        object-position: center;
    }

    &:hover {
        filter: brightness(115%);
    }
}

.section-col {
    padding: .5em;
    flex: 2;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;

    >.section-col-group {
        display: flex;
        flex-direction: row;
        justify-content: flex-start;
        align-items: flex-start;
        width: 100%;

        >.section-col-item {
            margin: .5em;
            flex: 1;
            display: flex;
            flex-direction: column;
            justify-content: flex-start;
            align-items: flex-start;

            >label {
                margin: 0 !important;
                padding: 0 !important;
                font-weight: bold;
                font-size: 1.2em;
                color: var(--sustainfinity-navy);
            }

            >input,
            select {
                padding: .25em;
                border-top: none;
                border-right: none;
                border-left: none;
                border-bottom: thin solid lightgray;
                background-color: whitesmoke;
                width: 100%;

                transition: border-bottom 500ms;

                &:focus {
                    border-bottom: thin solid black;
                }

                &:disabled {
                    border-radius: .2em;
                    background-color: rgb(221, 221, 221);
                    cursor: not-allowed;
                }
            }
        }
    }
}

.has-required-field {
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
    > div {
        word-break: normal;
        white-space: wrap;
        padding-left: .5em;
        font-size: .7em;
        color: var(--invalid-input) !important;
    }
}
</style>